import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';

import { cn } from '@talentmesh/ui/lib/utils';

function Tabs({ className, orientation = 'horizontal', ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            data-orientation={orientation}
            className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
            {...props}
        />
    );
}

const tabsListVariants = cva(
    'group/tabs-list inline-flex w-fit items-center justify-center rounded-none p-[3px] text-muted-foreground group-data-horizontal/tabs:h-9 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col',
    {
        variants: {
            variant: {
                default: 'bg-muted',
                line: 'gap-1 bg-transparent',
                'line-primary':
                    'h-auto min-h-0 gap-0 bg-transparent p-0 group-data-horizontal/tabs:h-auto group-data-horizontal/tabs:min-h-0',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function TabsList({
    className,
    variant = 'default',
    ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            data-variant={variant}
            className={cn(tabsListVariants({ variant }), className)}
            {...props}
        />
    );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(
                "text-foreground/60 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:text-muted-foreground dark:hover:text-foreground relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-none border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
                'data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
                'group-data-[variant=line]/tabs-list:after:bg-foreground after:absolute after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
                'group-data-[variant=line-primary]/tabs-list:text-muted-foreground group-data-[variant=line-primary]/tabs-list:h-auto group-data-[variant=line-primary]/tabs-list:flex-none group-data-[variant=line-primary]/tabs-list:shrink-0 group-data-[variant=line-primary]/tabs-list:border-x-0 group-data-[variant=line-primary]/tabs-list:border-t-0 group-data-[variant=line-primary]/tabs-list:border-b-2 group-data-[variant=line-primary]/tabs-list:border-transparent group-data-[variant=line-primary]/tabs-list:bg-transparent group-data-[variant=line-primary]/tabs-list:px-3 group-data-[variant=line-primary]/tabs-list:py-3 group-data-[variant=line-primary]/tabs-list:transition-colors group-data-[variant=line-primary]/tabs-list:after:hidden md:group-data-[variant=line-primary]/tabs-list:px-4',
                'group-data-[variant=line-primary]/tabs-list:hover:border-border group-data-[variant=line-primary]/tabs-list:hover:text-foreground',
                'group-data-[variant=line-primary]/tabs-list:data-active:border-primary group-data-[variant=line-primary]/tabs-list:data-active:text-foreground dark:group-data-[variant=line-primary]/tabs-list:data-active:border-primary group-data-[variant=line-primary]/tabs-list:data-active:bg-transparent group-data-[variant=line-primary]/tabs-list:data-active:font-semibold dark:group-data-[variant=line-primary]/tabs-list:data-active:bg-transparent',
                className,
            )}
            {...props}
        />
    );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn('flex-1 text-sm outline-none', className)}
            {...props}
        />
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
